<template>
    <div class="ggtf-container">
        <!-- 头部 -->
        <div class="ggtf-header">
            <h1 class="ggtf-title">广告投放</h1>
            <div class="ggtf-tabs">
                <button :class="['ggtf-tab', { 'ggtf-active': activeTab === 'list' }]" @click="activeTab = 'list'">
                    视频列表
                </button>
                <button :class="['ggtf-tab', { 'ggtf-active': activeTab === 'my' }]" @click="activeTab = 'my'">
                    我参与的
                </button>
            </div>
        </div>

        <!-- 视频列表 -->
        <div class="ggtf-video-list" v-if="activeTab === 'list'">
            <div v-for="video in videoList" :key="video.id" class="ggtf-video-card">
                <!-- 视频封面 -->
                <div class="ggtf-video-cover">
                    <img :src="video.cover" :alt="video.title" class="ggtf-cover-image">
                    <div class="ggtf-video-overlay">
                        <div class="ggtf-play-btn">
                            <i class="fas fa-play"></i>
                        </div>
                        <div class="ggtf-duration">{{ video.duration }}</div>
                    </div>
                </div>

                <!-- 视频信息 -->
                <div class="ggtf-video-info">
                    <h3 class="ggtf-video-title">{{ video.title }}</h3>
                    <div class="ggtf-activity-code">
                        活动编码: {{ video.activityCode }}
                    </div>

                    <!-- 倒计时 -->
                    <div class="ggtf-countdown">
                        <div class="ggtf-countdown-title">剩余时间</div>
                        <div class="ggtf-countdown-timer">
                            <div class="ggtf-time-unit">
                                <span class="ggtf-time-value">{{ countdown.days }}</span>
                                <span class="ggtf-time-label">天</span>
                            </div>
                            <div class="ggtf-time-separator">:</div>
                            <div class="ggtf-time-unit">
                                <span class="ggtf-time-value">{{ countdown.hours }}</span>
                                <span class="ggtf-time-label">时</span>
                            </div>
                            <div class="ggtf-time-separator">:</div>
                            <div class="ggtf-time-unit">
                                <span class="ggtf-time-value">{{ countdown.minutes }}</span>
                                <span class="ggtf-time-label">分</span>
                            </div>
                            <div class="ggtf-time-separator">:</div>
                            <div class="ggtf-time-unit">
                                <span class="ggtf-time-value">{{ countdown.seconds }}</span>
                                <span class="ggtf-time-label">秒</span>
                            </div>
                        </div>
                        <div class="ggtf-end-time">
                            结束时间: {{ formatDate(video.endTime) }}
                        </div>
                    </div>

                    <!-- 购买信息 -->
                    <div class="ggtf-purchase-info">
                        <div class="ggtf-min-purchase">
                            <i class="fas fa-tag"></i>
                            最低购买 ${{ video.minPurchase }}
                        </div>
                        <div class="ggtf-vip-requirement">
                            <i class="fas fa-crown"></i>
                            达到VIP{{ video.vipLevel }}级, 人均参与{{ video.participation }}次
                        </div>
                    </div>

                    <!-- 进度条 -->
                    <div class="ggtf-progress-section">
                        <div class="ggtf-progress-info">
                            <span class="ggtf-progress-text">购买进度</span>
                            <span class="ggtf-progress-percent">{{ video.progress }}%</span>
                        </div>
                        <div class="ggtf-progress-bar">
                            <div class="ggtf-progress-fill" :style="{ width: video.progress + '%' }"></div>
                        </div>
                        <div class="ggtf-progress-stats">
                            <span>已购 ${{ video.purchasedAmount }}</span>
                            <span>目标 ${{ video.targetAmount }}</span>
                        </div>
                    </div>

                    <!-- 操作按钮 -->
                    <div class="ggtf-action-section">
                        <button class="ggtf-buy-btn" :class="{ 'ggtf-disabled': !canPurchase(video) }"
                            @click="handlePurchase(video)">
                            <i class="fas fa-bolt"></i>
                            抢购
                        </button>
                        <button class="ggtf-detail-btn" @click="showVideoDetail(video)">
                            <i class="fas fa-info-circle"></i>
                            详情
                        </button>
                    </div>
                </div>
            </div>

            <!-- 加载更多 -->
            <div class="ggtf-load-more">
                <button class="ggtf-load-more-btn" @click="loadMore" v-if="hasMore">
                    <i class="fas fa-spinner" v-if="loading"></i>
                    {{ loading ? '加载中...' : '加载更多' }}
                </button>
                <div class="ggtf-no-more" v-else>
                    没有更多视频了
                </div>
            </div>
        </div>

        <!-- 我参与的 -->
        <div class="ggtf-my-participation" v-else>
            <div class="ggtf-empty-state" v-if="myVideos.length === 0">
                <div class="ggtf-empty-icon">
                    <i class="fas fa-film"></i>
                </div>
                <h3>暂无参与记录</h3>
                <p>您还没有参与任何广告投放活动</p>
                <button class="ggtf-browse-btn" @click="activeTab = 'list'">
                    去浏览活动
                </button>
            </div>

            <div v-else>
                <div v-for="video in myVideos" :key="video.id" class="ggtf-my-video-card">
                    <div class="ggtf-my-video-cover">
                        <img :src="video.cover" :alt="video.title" class="ggtf-cover-image">
                    </div>
                    <div class="ggtf-my-video-info">
                        <h4 class="ggtf-my-video-title">{{ video.title }}</h4>
                        <div class="ggtf-my-activity-code">活动编码: {{ video.activityCode }}</div>

                        <div class="ggtf-my-stats">
                            <div class="ggtf-my-stat">
                                <span class="ggtf-stat-label">投资金额</span>
                                <span class="ggtf-stat-value">${{ video.myInvestment }}</span>
                            </div>
                            <div class="ggtf-my-stat">
                                <span class="ggtf-stat-label">预计收益</span>
                                <span class="ggtf-stat-value ggtf-profit">+${{ video.expectedProfit }}</span>
                            </div>
                            <div class="ggtf-my-stat">
                                <span class="ggtf-stat-label">参与时间</span>
                                <span class="ggtf-stat-value">{{ formatDate(video.participateTime) }}</span>
                            </div>
                        </div>

                        <div class="ggtf-status-badge" :class="'ggtf-status-' + video.status">
                            {{ getStatusText(video.status) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 视频详情弹窗 -->
        <div v-if="selectedVideo" class="ggtf-modal-overlay" @click="selectedVideo = null">
            <div class="ggtf-modal-content" @click.stop>
                <div class="ggtf-modal-header">
                    <h3>活动详情</h3>
                    <button class="ggtf-close-btn" @click="selectedVideo = null">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="ggtf-modal-body">
                    <div class="ggtf-detail-cover">
                        <img :src="selectedVideo.cover" :alt="selectedVideo.title" class="ggtf-detail-image">
                    </div>
                    <div class="ggtf-detail-info">
                        <h4 class="ggtf-detail-title">{{ selectedVideo.title }}</h4>
                        <div class="ggtf-detail-code">活动编码: {{ selectedVideo.activityCode }}</div>

                        <div class="ggtf-detail-section">
                            <h5>活动介绍</h5>
                            <p class="ggtf-detail-desc">{{ selectedVideo.description }}</p>
                        </div>

                        <div class="ggtf-detail-section">
                            <h5>收益规则</h5>
                            <ul class="ggtf-rules-list">
                                <li v-for="(rule, index) in selectedVideo.rules" :key="index">
                                    <i class="fas fa-check"></i>
                                    {{ rule }}
                                </li>
                            </ul>
                        </div>

                        <div class="ggtf-detail-section">
                            <h5>参与要求</h5>
                            <div class="ggtf-requirements">
                                <div class="ggtf-requirement">
                                    <i class="fas fa-dollar-sign"></i>
                                    最低投资: ${{ selectedVideo.minPurchase }}
                                </div>
                                <div class="ggtf-requirement">
                                    <i class="fas fa-crown"></i>
                                    VIP等级: {{ selectedVideo.vipLevel }}级
                                </div>
                                <div class="ggtf-requirement">
                                    <i class="fas fa-users"></i>
                                    人均参与: {{ selectedVideo.participation }}次
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="ggtf-modal-footer">
                    <button class="ggtf-cancel-btn" @click="selectedVideo = null">取消</button>
                    <button class="ggtf-confirm-btn" @click="handlePurchase(selectedVideo)">
                        立即参与
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AdDelivery',
    data() {
        return {
            activeTab: 'list',
            selectedVideo: null,
            loading: false,
            hasMore: true,
            countdown: {
                days: '00',
                hours: '06',
                minutes: '27',
                seconds: '55'
            },
            videoList: [
                {
                    id: 1,
                    title: 'The Beggar King Returns Full Movie',
                    activityCode: 'VA50067270',
                    cover: 'https://via.placeholder.com/300x180/4A5568/FFFFFF?text=Movie+Cover',
                    duration: '02:15:30',
                    endTime: new Date('2025-11-15 07:24:00'),
                    minPurchase: 151,
                    vipLevel: 1,
                    participation: 1,
                    progress: 65,
                    purchasedAmount: 98350,
                    targetAmount: 150000,
                    description: '这是一部精彩的电影广告投放活动，参与即可获得丰厚收益。',
                    rules: [
                        '投资金额达到$151即可参与',
                        'VIP1级以上用户可参与',
                        '收益按日结算，随时可提现',
                        '活动结束后统一发放奖励'
                    ]
                },
                {
                    id: 2,
                    title: 'Epic Adventure: Lost Kingdom',
                    activityCode: 'VA50067271',
                    cover: 'https://via.placeholder.com/300x180/2D3748/FFFFFF?text=Adventure',
                    duration: '01:45:20',
                    endTime: new Date('2025-11-20 12:00:00'),
                    minPurchase: 200,
                    vipLevel: 2,
                    participation: 2,
                    progress: 42,
                    purchasedAmount: 75600,
                    targetAmount: 180000,
                    description: '冒险题材电影广告，高收益投资机会。',
                    rules: [
                        '投资金额达到$200即可参与',
                        'VIP2级以上用户可参与',
                        '收益按周结算',
                        '提前退出收取10%手续费'
                    ]
                },
                {
                    id: 3,
                    title: 'Sci-Fi Chronicles: Future World',
                    activityCode: 'VA50067272',
                    cover: 'https://via.placeholder.com/300x180/1A202C/FFFFFF?text=Sci-Fi',
                    duration: '02:30:15',
                    endTime: new Date('2025-11-25 18:30:00'),
                    minPurchase: 100,
                    vipLevel: 1,
                    participation: 1,
                    progress: 78,
                    purchasedAmount: 62400,
                    targetAmount: 80000,
                    description: '科幻大片广告投放，限量参与名额。',
                    rules: [
                        '投资金额达到$100即可参与',
                        'VIP1级以上用户可参与',
                        '收益按日结算',
                        '名额有限，先到先得'
                    ]
                }
            ],
            myVideos: [
                {
                    id: 1,
                    title: 'The Beggar King Returns Full Movie',
                    activityCode: 'VA50067270',
                    cover: 'https://via.placeholder.com/300x180/4A5568/FFFFFF?text=Movie+Cover',
                    myInvestment: 200,
                    expectedProfit: 25.5,
                    participateTime: new Date('2025-11-10 14:30:00'),
                    status: 'active'
                },
                {
                    id: 3,
                    title: 'Sci-Fi Chronicles: Future World',
                    activityCode: 'VA50067272',
                    cover: 'https://via.placeholder.com/300x180/1A202C/FFFFFF?text=Sci-Fi',
                    myInvestment: 150,
                    expectedProfit: 18.2,
                    participateTime: new Date('2025-11-08 09:15:00'),
                    status: 'completed'
                }
            ]
        }
    },
    computed: {
        userVIPLevel() {
            return 2 // 模拟用户VIP等级
        }
    },
    mounted() {
        this.startCountdown()
    },
    methods: {
        startCountdown() {
            setInterval(() => {
                // 模拟倒计时更新
                let seconds = parseInt(this.countdown.seconds)
                let minutes = parseInt(this.countdown.minutes)
                let hours = parseInt(this.countdown.hours)
                let days = parseInt(this.countdown.days)

                seconds--

                if (seconds < 0) {
                    seconds = 59
                    minutes--

                    if (minutes < 0) {
                        minutes = 59
                        hours--

                        if (hours < 0) {
                            hours = 23
                            days--
                        }
                    }
                }

                this.countdown = {
                    days: days.toString().padStart(2, '0'),
                    hours: hours.toString().padStart(2, '0'),
                    minutes: minutes.toString().padStart(2, '0'),
                    seconds: seconds.toString().padStart(2, '0')
                }
            }, 1000)
        },

        formatDate(date) {
            return new Date(date).toLocaleDateString('zh-CN') + ' ' +
                new Date(date).toLocaleTimeString('zh-CN', {
                    hour: '2-digit',
                    minute: '2-digit'
                })
        },

        canPurchase(video) {
            return this.userVIPLevel >= video.vipLevel
        },

        handlePurchase(video) {
            if (!this.canPurchase(video)) {
                this.$message.error(`需要VIP${video.vipLevel}级以上才能参与`)
                return
            }

            this.$confirm(`确定要参与 "${video.title}" 活动吗？`, '确认参与', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                this.$message.success('参与成功！')
                // 这里可以添加实际参与逻辑
            })
        },

        showVideoDetail(video) {
            this.selectedVideo = video
        },

        loadMore() {
            this.loading = true
            // 模拟加载更多数据
            setTimeout(() => {
                this.videoList.push(...this.videoList.slice(0, 2).map((video, index) => ({
                    ...video,
                    id: this.videoList.length + index + 1,
                    activityCode: `VA50067${270 + this.videoList.length + index}`
                })))
                this.loading = false
                this.hasMore = this.videoList.length < 10
            }, 1000)
        },

        getStatusText(status) {
            const statusMap = {
                'active': '进行中',
                'completed': '已完成',
                'pending': '待结算'
            }
            return statusMap[status] || status
        }
    }
}
</script>

<style scoped>
.ggtf-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 24px;
    background: #f8fafc;
    min-height: 100vh;
}

.ggtf-header {
    background: white;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.ggtf-title {
    font-size: 28px;
    font-weight: 700;
    color: #1a202c;
    margin: 0;
}

.ggtf-tabs {
    display: flex;
    background: #f7fafc;
    border-radius: 12px;
    padding: 4px;
}

.ggtf-tab {
    padding: 12px 24px;
    border: none;
    background: transparent;
    color: #718096;
    font-weight: 500;
    cursor: pointer;
    border-radius: 8px;
    transition: all 0.3s;
}

.ggtf-tab.ggtf-active {
    background: white;
    color: #4299e1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.ggtf-video-list {
    display: grid;
    gap: 24px;
}

.ggtf-video-card {
    background: white;
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    display: flex;
    transition: all 0.3s;
}

.ggtf-video-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.ggtf-video-cover {
    position: relative;
    width: 300px;
    flex-shrink: 0;
}

.ggtf-cover-image {
    width: 100%;
    height: 200px;
    object-fit: cover;
}

.ggtf-video-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s;
}

.ggtf-video-cover:hover .ggtf-video-overlay {
    opacity: 1;
}

.ggtf-play-btn {
    width: 60px;
    height: 60px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #4299e1;
    font-size: 20px;
}

.ggtf-duration {
    position: absolute;
    bottom: 12px;
    right: 12px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
}

.ggtf-video-info {
    flex: 1;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.ggtf-video-title {
    font-size: 20px;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
}

.ggtf-activity-code {
    color: #718096;
    font-size: 14px;
}

.ggtf-countdown {
    background: #f7fafc;
    border-radius: 12px;
    padding: 16px;
}

.ggtf-countdown-title {
    color: #718096;
    font-size: 14px;
    margin-bottom: 8px;
}

.ggtf-countdown-timer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 8px;
}

.ggtf-time-unit {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.ggtf-time-value {
    background: #4299e1;
    color: white;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 18px;
    font-weight: 700;
    min-width: 40px;
    text-align: center;
}

.ggtf-time-label {
    font-size: 12px;
    color: #718096;
}

.ggtf-time-separator {
    color: #4299e1;
    font-weight: 700;
    margin-top: -12px;
}

.ggtf-end-time {
    text-align: center;
    color: #718096;
    font-size: 12px;
}

.ggtf-purchase-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.ggtf-min-purchase,
.ggtf-vip-requirement {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #4a5568;
    font-size: 14px;
}

.ggtf-min-purchase i {
    color: #48bb78;
}

.ggtf-vip-requirement i {
    color: #ed8936;
}

.ggtf-progress-section {
    background: #f7fafc;
    border-radius: 8px;
    padding: 12px;
}

.ggtf-progress-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.ggtf-progress-text {
    color: #718096;
    font-size: 14px;
}

.ggtf-progress-percent {
    color: #4299e1;
    font-weight: 600;
}

.ggtf-progress-bar {
    width: 100%;
    height: 8px;
    background: #e2e8f0;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 8px;
}

.ggtf-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #4299e1, #3182ce);
    border-radius: 4px;
    transition: width 0.3s;
}

.ggtf-progress-stats {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #718096;
}

.ggtf-action-section {
    display: flex;
    gap: 12px;
}

.ggtf-buy-btn,
.ggtf-detail-btn {
    flex: 1;
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.3s;
}

.ggtf-buy-btn {
    background: linear-gradient(135deg, #ed8936, #dd6b20);
    color: white;
}

.ggtf-buy-btn:hover:not(.ggtf-disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(237, 137, 54, 0.3);
}

.ggtf-buy-btn.ggtf-disabled {
    background: #cbd5e0;
    cursor: not-allowed;
    transform: none;
}

.ggtf-detail-btn {
    background: white;
    color: #4a5568;
    border: 1px solid #e2e8f0;
}

.ggtf-detail-btn:hover {
    background: #f7fafc;
    border-color: #cbd5e0;
}

.ggtf-load-more {
    text-align: center;
    padding: 24px;
}

.ggtf-load-more-btn {
    padding: 12px 32px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: white;
    color: #4a5568;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s;
}

.ggtf-load-more-btn:hover {
    background: #f7fafc;
    border-color: #cbd5e0;
}

.ggtf-no-more {
    color: #a0aec0;
    font-size: 14px;
}

/* 我参与的样式 */
.ggtf-my-participation {
    background: white;
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    overflow: hidden;
}

.ggtf-empty-state {
    text-align: center;
    padding: 80px 20px;
    color: #a0aec0;
}

.ggtf-empty-icon {
    font-size: 64px;
    margin-bottom: 20px;
    color: #cbd5e0;
}

.ggtf-empty-state h3 {
    color: #718096;
    margin-bottom: 8px;
}

.ggtf-empty-state p {
    margin-bottom: 24px;
}

.ggtf-browse-btn {
    padding: 12px 24px;
    border: 1px solid #4299e1;
    border-radius: 8px;
    background: #4299e1;
    color: white;
    cursor: pointer;
    transition: all 0.3s;
}

.ggtf-browse-btn:hover {
    background: #3182ce;
    border-color: #3182ce;
}

.ggtf-my-video-card {
    display: flex;
    padding: 24px;
    border-bottom: 1px solid #f7fafc;
    transition: all 0.3s;
}

.ggtf-my-video-card:hover {
    background: #f8fafc;
}

.ggtf-my-video-cover {
    width: 120px;
    height: 80px;
    flex-shrink: 0;
    margin-right: 20px;
}

.ggtf-my-video-cover .ggtf-cover-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
}

.ggtf-my-video-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.ggtf-my-video-title {
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
}

.ggtf-my-activity-code {
    color: #718096;
    font-size: 12px;
}

.ggtf-my-stats {
    display: flex;
    gap: 24px;
}

.ggtf-my-stat {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.ggtf-stat-label {
    font-size: 12px;
    color: #718096;
}

.ggtf-stat-value {
    font-size: 14px;
    font-weight: 600;
    color: #2d3748;
}

.ggtf-stat-value.ggtf-profit {
    color: #48bb78;
}

.ggtf-status-badge {
    align-self: flex-start;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
}

.ggtf-status-active {
    background: #c6f6d5;
    color: #22543d;
}

.ggtf-status-completed {
    background: #bee3f8;
    color: #2a4365;
}

.ggtf-status-pending {
    background: #feebc8;
    color: #744210;
}

/* 模态框样式 */
.ggtf-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
}

.ggtf-modal-content {
    background: white;
    border-radius: 16px;
    width: 600px;
    max-width: 90vw;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.ggtf-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px;
    border-bottom: 1px solid #e2e8f0;
}

.ggtf-modal-header h3 {
    margin: 0;
    color: #2d3748;
    font-size: 20px;
    font-weight: 600;
}

.ggtf-close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: none;
    color: #718096;
    cursor: pointer;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;
}

.ggtf-close-btn:hover {
    background: #f7fafc;
    color: #4a5568;
}

.ggtf-modal-body {
    padding: 24px;
}

.ggtf-detail-cover {
    text-align: center;
    margin-bottom: 20px;
}

.ggtf-detail-image {
    width: 300px;
    height: 180px;
    object-fit: cover;
    border-radius: 12px;
}

.ggtf-detail-info {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.ggtf-detail-title {
    font-size: 18px;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
}

.ggtf-detail-code {
    color: #718096;
    font-size: 14px;
}

.ggtf-detail-section h5 {
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
    margin: 0 0 12px 0;
}

.ggtf-detail-desc {
    color: #4a5568;
    line-height: 1.6;
    margin: 0;
}

.ggtf-rules-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.ggtf-rules-list li {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    color: #4a5568;
}

.ggtf-rules-list li i {
    color: #48bb78;
}

.ggtf-requirements {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.ggtf-requirement {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #4a5568;
}

.ggtf-requirement i {
    color: #ed8936;
}

.ggtf-modal-footer {
    padding: 20px 24px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}

.ggtf-cancel-btn,
.ggtf-confirm-btn {
    padding: 10px 20px;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s;
}

.ggtf-cancel-btn {
    background: white;
    color: #4a5568;
    border: 1px solid #e2e8f0;
}

.ggtf-cancel-btn:hover {
    background: #f7fafc;
}

.ggtf-confirm-btn {
    background: linear-gradient(135deg, #ed8936, #dd6b20);
    color: white;
    border: none;
}

.ggtf-confirm-btn:hover {
    background: linear-gradient(135deg, #dd6b20, #c05621);
}

@media (max-width: 768px) {
    .ggtf-container {
        padding: 16px;
    }

    .ggtf-header {
        flex-direction: column;
        gap: 16px;
        align-items: flex-start;
    }

    .ggtf-video-card {
        flex-direction: column;
    }

    .ggtf-video-cover {
        width: 100%;
    }

    .ggtf-my-video-card {
        flex-direction: column;
        gap: 16px;
    }

    .ggtf-my-video-cover {
        width: 100%;
        height: 120px;
        margin-right: 0;
    }

    .ggtf-my-stats {
        flex-direction: column;
        gap: 12px;
    }
}
</style>